import { usePopover } from '@/hooks/usePopover';
import { useFileDownload } from '@/hooks/useFileDownload';
import { useTenants } from '@/services/tenants/useTenants';
import { DownloadFormat } from '@/utils/downloadUtils';
import { FileDownloadOutlined, InsertDriveFileOutlined } from '@mui/icons-material';
import { CircularProgress, Divider, IconButton, ListItemIcon, Tooltip } from '@mui/material';
import { useCallback } from 'react';
import { StyledList, StyledListHeader, StyledListItem } from '@/components/AssetsPage/PopoverList';
import { BodyText } from '@/components/BodyText';
import { RoundedPopover } from '@/components/Popover/RoundedPopover';

interface TaxonomyExportButtonProperties {
  taxonomyId: string;
}

export const TaxonomyExportButton = ({ taxonomyId }: TaxonomyExportButtonProperties) => {
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();

  const { open: openMenu, close: closeMenu, isOpen: isMenuOpen, anchorElement: menuAnchorElement } = usePopover();
  const { download, isDownloading } = useFileDownload();

  const handleDownload = useCallback(
    async (format: DownloadFormat) => {
      if (!taxonomyId || !tenantId) return;

      closeMenu();
      await download(`/tenants/${tenantId}/taxonomies/${taxonomyId}/nodes/export?format=${format}`, {
        filename: `taxonomy-export.${format}`,
        errorMessage: `Failed to download ${format.toUpperCase()}`,
      });
    },
    [taxonomyId, tenantId, closeMenu, download]
  );

  const formats: DownloadFormat[] = ['csv', 'xlsx'];
  const formatLabels: Record<string, string> = {
    csv: 'CSV',
    xlsx: 'XLSX',
  };

  return (
    <>
      <Tooltip title='Export'>
        <span>
          <IconButton size='small' onClick={openMenu} disabled={isDownloading}>
            {isDownloading ? <CircularProgress size={18} /> : <FileDownloadOutlined sx={{ fontSize: '18px' }} />}
          </IconButton>
        </span>
      </Tooltip>
      <RoundedPopover
        open={isMenuOpen}
        anchorEl={menuAnchorElement}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              marginTop: '4px',
              borderRadius: '12px',
            },
          },
        }}
      >
        <StyledList>
          <StyledListHeader>
            <BodyText>Export Taxonomy</BodyText>
          </StyledListHeader>
          <Divider />
          {formats.map(format => (
            <StyledListItem key={format} onClick={() => void handleDownload(format)} sx={{ cursor: 'pointer' }}>
              <ListItemIcon>
                <InsertDriveFileOutlined color='action' sx={{ fontSize: '18px' }} />
              </ListItemIcon>
              <BodyText>{formatLabels[format]}</BodyText>
            </StyledListItem>
          ))}
        </StyledList>
      </RoundedPopover>
    </>
  );
};
